import { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Resizer from 'react-image-file-resizer';
import api from '../api/api';

/**
 * Custom hook to manage file uploads and image capture
 * @param {Function} onFileProcessed - Callback when file is processed by Gemini
 * @returns {Object} File upload handlers and refs
 */
export const useFileUpload = (onFileProcessed) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files?.[0];
    return file;
  }, []);

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleImageCapture = useCallback(
    (categories, accounts) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment'; // Opens rear camera on mobile devices

      input.click();

      input.onchange = async (event) => {
        const file = event.target.files?.[0];
        if (file) {
          Resizer.imageFileResizer(
            file,
            1280,
            720,
            'JPEG',
            80,
            0,
            async (uri) => {
              try {
                dispatch({ type: 'appMain/loadingStart' });
                const generatedData = await api.loadTransactionByFile(uri);
                
                if (generatedData && onFileProcessed) {
                  onFileProcessed(generatedData, categories, accounts);
                }
              } catch (error) {
                console.error('Error processing image:', error);
              } finally {
                dispatch({ type: 'appMain/loadingEnd' });
              }
            },
            'file'
          );
        }
      };
    },
    [dispatch, onFileProcessed]
  );

  const deleteAttachment = useCallback(async (transactionId) => {
    try {
      console.log('Delete attachment for transaction id:', transactionId);
      // TODO: Implement actual API call
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  }, []);

  return {
    fileInputRef,
    handleUploadClick,
    handleFileChange,
    clearFileInput,
    handleImageCapture,
    deleteAttachment,
  };
};
